import {Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {forkJoin} from 'rxjs';
import {Modal} from '@shared/components/common/modal/modal';
import {ErrorService} from '@shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {FormStatusService} from '@shared/services/form-status.service';
import {CombosService} from '@shared/services/combos.service';
import {ClientesService} from '../clientes.service';
import {Cliente, ClienteUpdatePayload} from '../clientes.interface';
import {DocumentoTipo, GenericCombo, Pais} from '@shared/services/interfaces/combos.interface';
import {Badge} from '@shared/components/ui/badge/badge';
// import {DatosFacturacion} from '../../datos-facturacion/datos-facturacion';
import {LabelComponent} from '@shared/components/form/label/label.component';
import {EditionButtons} from '@shared/components/ui/edition-buttons/edition-buttons';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import {AppFormComponent} from '@shared/components/ui/list-components';
import {ModalConfirmationService} from '@shared/services/modal-confirmation.service';

/**
 * Datos del cliente (la facturación tiene su propia línea en la ficha). Antes era la cabecera de la ficha; desde el
 * rediseño de la ficha (2026-10-05) se abre con el lápiz y cierra con `true` si se guardó algo.
 */
@Component({
  selector: 'app-datos-cliente-modal',
  imports: [
    HasPermissionDirective,
    Modal,
    ReactiveFormsModule,
    Badge,
    // DatosFacturacion,
    LabelComponent,
    EditionButtons,
    SelectFieldComponent,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './datos-cliente-modal.html',
})
export class DatosClienteModal implements OnInit {

  idCliente: number = inject(MAT_DIALOG_DATA);
  /** Se guardó algo: la ficha tiene que refrescar su cabecera al cerrar. */
  cambio = false;
  loading = false;
  process = false;
  cliente: Cliente | null = null;
  paisNombre: string = '';

  editForm!: FormGroup;
  editable = true;
  documentosTipos: DocumentoTipo[] = [];
  paises: Pais[] = [];
  tiposClientes: GenericCombo[] = [];

  // Issue #24 (2026-09-15): sexo y fecha de nacimiento ya no se muestran (los bloques
  // están comentados en el HTML), pero los controles y el payload siguen intactos a
  // propósito: así una edición reenvía el valor guardado en vez de borrarlo.
  sexoOptions: SelectOption[] = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
  ];

  get documentosTiposOptions(): SelectOption[] {
    return this.documentosTipos.map(d => ({ value: d.id, label: d.name }));
  }

  get paisesOptions(): SelectOption[] {
    return this.paises.map(p => ({ value: p.id, label: p.nombre }));
  }

  get tiposClientesOptions(): SelectOption[] {
    return this.tiposClientes.map(c => ({ value: c.id, label: c.nombre }));
  }

  get esFisica(): boolean {
    return this.cliente?.persona.persona_tipo === 'F';
  }

  /**
   * Los clientes dados de alta desde que se sacó el campo guardan el nombre completo en
   * `nombre` y tienen `apellido` en null. Los viejos lo siguen teniendo cargado, y ahí el
   * campo se muestra para poder corregirlo.
   */
  get tieneApellido(): boolean {
    return !!this.cliente?.persona.persona_tipo_detalle?.apellido;
  }

  get nombreCompleto(): string {
    const detalle = this.cliente?.persona.persona_tipo_detalle;
    return [detalle?.nombre, detalle?.apellido].filter(Boolean).join(' ');
  }

  // app-select emite siempre string (el <option [value]> nativo stringifica), así que
  // los ids de los combos se normalizan a número antes de compararlos o mandarlos.
  private aId(valor: unknown): number | null {
    return valor === '' || valor === null || valor === undefined ? null : Number(valor);
  }

  // El tipo de cliente define la tarifa. Si el usuario lo cambia se le pide confirmación
  // antes de guardar, porque el sistema no lleva auditoría de quién lo modificó.
  get tipoClienteCambio(): boolean {
    const actual = this.cliente?.param_tipo_cliente_id ?? null;
    return this.aId(this.editForm?.get('param_tipo_cliente_id')?.value) !== actual;
  }

  get tipoClienteNuevoNombre(): string {
    const nuevo = this.aId(this.editForm?.get('param_tipo_cliente_id')?.value);
    return this.tiposClientes.find(t => t.id === nuevo)?.nombre ?? 'Sin tipo asignado';
  }

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private dialogRef = inject(MatDialogRef<DatosClienteModal>);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private combosService = inject(CombosService);
  private clientesService = inject(ClientesService);
  private modalConfirmationService = inject(ModalConfirmationService);

  ngOnInit(): void {
    this.makeForm();
    this.getCombos();
    this.getCliente();
  }

  private makeForm(): void {
    // Todos los campos arrancan deshabilitados; los habilita editableSwitcher().
    this.editForm = this.fB.group({
      nombre: [{ value: '', disabled: this.editable }, Validators.required],
      apellido: [{ value: '', disabled: this.editable }],
      nombre_fantasia: [{ value: '', disabled: this.editable }],
      fecha_nacimiento: [{ value: '', disabled: this.editable }],
      fecha_constitucion: [{ value: '', disabled: this.editable }],
      sexo: [{ value: '', disabled: this.editable }],
      per_documento_tipo_id: [{ value: '', disabled: this.editable }, Validators.required],
      // Issue #23: la ubicación dejó de ser obligatoria.
      ubi_pais_id: [{ value: '', disabled: this.editable }],
      numero: [{ value: '', disabled: this.editable }, Validators.required],
      param_tipo_cliente_id: [{ value: '', disabled: this.editable }],
    });
  }

  private getCombos(): void {
    const documentosTipos = this.combosService.getDocumentoTipoCombo();
    const paises = this.combosService.getPaisesCombo();
    const tiposClientes = this.combosService.getTiposClientesCombo();

    forkJoin([documentosTipos, paises, tiposClientes]).subscribe({
      next: (response) => {
        this.documentosTipos = response[0].data;
        this.paises = response[1].data;
        this.tiposClientes = response[2].data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  getCliente(): void {
    this.loading = true;
    this.clientesService.show(this.idCliente).subscribe({
      next: (response) => {
        if (response.status) {
          this.cliente = response.data;

          // Extraer el nombre del país del campo documento_completo.
          // Formato: "numero/tipo/pais", o "numero/tipo" si el documento se cargó sin
          // ubicación (dejó de ser obligatoria en el issue #23).
          const documentoCompleto = this.cliente.persona.documento_principal.documento_completo;
          const partes = documentoCompleto.split('/');
          this.paisNombre = partes.length === 3 ? partes[2] : 'Sin datos';

          this.patchForm(this.cliente);
          // El lápiz de la ficha abre directo en edición
          if (this.editable) this.editableSwitcher();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  private patchForm(cliente: Cliente): void {
    const detalle = cliente.persona.persona_tipo_detalle;
    const documento = cliente.persona.documento_principal;

    this.editForm.patchValue({
      nombre: detalle.nombre ?? '',
      apellido: detalle.apellido ?? '',
      nombre_fantasia: detalle.nombre_fantasia ?? '',
      fecha_nacimiento: detalle.fecha_nacimiento ?? '',
      fecha_constitucion: detalle.fecha_constitucion ?? '',
      sexo: detalle.sexo ?? '',
      per_documento_tipo_id: documento.per_documento_tipo_id,
      ubi_pais_id: documento.ubi_pais_id,
      numero: documento.numero,
      param_tipo_cliente_id: cliente.param_tipo_cliente_id ?? '',
    });
  }

  updateCliente(): void {
    // Cambiar el tipo de cliente cambia la tarifa que se aplica a los servicios que se
    // coticen de acá en adelante, y el sistema no guarda quién lo hizo. Se confirma aparte.
    if (!this.tipoClienteCambio) {
      this.enviarActualizacion();
      return;
    }

    this.modalConfirmationService.open({
      title: 'Cambiar el tipo de cliente',
      message: `El tipo de cliente pasa a "${this.tipoClienteNuevoNombre}". Esto cambia la tarifa que se aplicará a los próximos servicios de este cliente. Los presupuestos ya emitidos no se modifican. ¿Confirma el cambio?`,
      actions: { confirm: { label: 'Confirmar cambio' } },
    }).afterClosed().subscribe((result) => {
      if (result !== 'confirmed') {
        return;
      }

      this.enviarActualizacion();
    });
  }

  private enviarActualizacion(): void {
    this.process = true;

    const valores = this.editForm.value;
    const jsonData: ClienteUpdatePayload = {
      nombre: valores.nombre,
      per_documento_tipo_id: this.aId(valores.per_documento_tipo_id) ?? undefined,
      // null y no undefined: con undefined el campo no viajaba y vaciar la ubicación
      // no se podía guardar. El backend la acepta nullable desde el issue #23.
      ubi_pais_id: this.aId(valores.ubi_pais_id),
      numero: valores.numero,
      param_tipo_cliente_id: this.aId(valores.param_tipo_cliente_id),
    };

    // Solo se mandan los campos del tipo de persona que corresponde; el backend
    // valida contra per_fisicas o per_juridicas según lo que ya está guardado.
    if (this.esFisica) {
      jsonData.apellido = valores.apellido;
      jsonData.fecha_nacimiento = valores.fecha_nacimiento || null;
      jsonData.sexo = valores.sexo || null;
    } else {
      jsonData.nombre_fantasia = valores.nombre_fantasia || null;
      jsonData.fecha_constitucion = valores.fecha_constitucion || null;
    }

    this.clientesService.update(this.idCliente, jsonData).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Cliente actualizado correctamente');
          this.dialogRef.close(true);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.process = false;
    });
  }

  refreshCliente(): void {
    this.getCliente();
    this.editableSwitcher();
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  get titulo(): string {
    if (!this.cliente) return 'Cliente';
    return this.esFisica ? this.nombreCompleto : this.cliente.persona.persona_tipo_detalle.nombre;
  }

  cerrar(): void {
    this.dialogRef.close(this.cambio);
  }
}
